import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import MaskedPii from '../components/MaskedPii';
import { useConfirm } from '../components/ConfirmDialog';
import { formatDateTime } from '../lib/format';
import { useAuth } from '../lib/auth';
import { buildHaystack, matchesAllTokens, tokenizeQuery } from '../lib/search';

// قايمة كل المتطوعين + شاشة HR للمراجعة والقرارات + استيراد CSV.
// v2: بيدعم الحقول الجديدة الجاية من CSV المؤسسة القديم — رقم واتساب،
// تاريخ التواصل، كود الاستمارة، الأيام واللجان، سيارة، إلخ.

type Row = {
  id: number;
  user_id: string | null;
  full_name: string;
  gender: 'MALE' | 'FEMALE' | null;
  phone: string | null;
  whatsapp_phone: string | null;
  guardian_phone: string | null;
  email: string | null;
  // SPECS/002 Batch 35: NID is intentionally NOT part of Row. The
  // plaintext column is REVOKEd from authenticated; the modal
  // fetches it on-demand via decrypt_volunteer_nid_for_admin (which
  // logs the access). Any future reference to `r.national_id` on
  // the list page is a regression — the migration guarantees the
  // field won't come back over PostgREST anyway, but typing it out
  // of Row stops the TS compiler from pretending it will.
  address: string | null;
  specialty_id: number | null;
  governorate_id: number | null;
  branch_id: number | null;
  specialty_text: string | null;
  governorate_text: string | null;
  branch_text: string | null;
  application_status: 'REVIEW' | 'HR' | 'ACCEPTED' | 'REJECTED' | 'STOPPED';
  hr_decision: 'PENDING' | 'CONTINUE' | 'STOPPED';
  hr_note: string | null;
  has_car: boolean | null;
  car_available_for_volunteering: boolean | null;
  prior_experience: boolean | null;
  skills: string | null;
  contacted: boolean | null;
  contacted_at: string | null;
  registration_form_type: string | null;
  days_and_committees: string | null;
  external_form_id: string | null;
  external_form_code: string | null;
  created_at: string;
};

type Ref = { id: number; name: string };

const STATUS_LABEL: Record<Row['application_status'], string> = {
  REVIEW: 'قيد المراجعة', HR: 'لدى HR', ACCEPTED: 'مقبول', REJECTED: 'مرفوض', STOPPED: 'متوقف',
};

type ImportResult = {
  ok?: boolean;
  parsed_rows?: number;
  imported_after_dedup?: number;
  inserted?: number;
  skipped_no_phone?: number;
  skipped_duplicate_or_existing?: number;
  errors?: string[];
  error?: string;
};

// filterQueue: when routed from the volunteering wrapper's approval
// tabs, pre-select the corresponding status and hide the top status
// selector so the tab reads as "these rows are waiting on you".
type FilterQueue = 'coordinator' | 'hr';
export default function VolunteersPage({ filterQueue }: { filterQueue?: FilterQueue } = {}) {
  const { permissions } = useAuth();
  const canManage = permissions.canVolunteering;
  // Granular approval permissions: each REVIEW-tab operator only sees
  // the approve/reject buttons for their queue. Full manage still
  // implies both (backfill in the DB migration matches this).
  const canApproveCoordinator = permissions.canApproveVolunteeringCoordinator;
  const canApproveHr = permissions.canApproveVolunteeringHr;
  // A signed-in admin can act on the *currently selected* row if they
  // hold the approval flag for its current queue.
  const canActOnSelected = (row: Row | null) =>
    !!row && (
      canManage
      || (row.application_status === 'REVIEW' && canApproveCoordinator)
      || (row.application_status === 'HR' && canApproveHr)
    );
  const [rows, setRows] = useState<Row[]>([]);
  const [specialties, setSpecialties] = useState<Ref[]>([]);
  const [governorates, setGovernorates] = useState<Ref[]>([]);
  const [branches, setBranches] = useState<Ref[]>([]);
  const initialStatus: 'ALL' | Row['application_status'] =
    filterQueue === 'coordinator' ? 'REVIEW'
    : filterQueue === 'hr' ? 'HR'
    : 'ALL';
  const [statusFilter, setStatusFilter] = useState<'ALL' | Row['application_status']>(initialStatus);
  const [branchFilter, setBranchFilter] = useState<string>('ALL');
  const [govFilter, setGovFilter] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Row | null>(null);
  // SPECS/002: NID is encrypted at rest; fetched on demand via a
  // SECURITY DEFINER RPC that gates on volunteering section access
  // and writes an audit row. Fetch happens on EXPLICIT reveal
  // (operator clicks the "اعرض الرقم القومي" button), so the audit
  // trail records intent-to-view rather than modal-open noise.
  // null = not yet revealed; '' = fetched but no NID on file;
  // non-empty = decrypted plaintext.
  const [selectedNid, setSelectedNid] = useState<string | null>(null);
  const [selectedNidLoading, setSelectedNidLoading] = useState(false);
  const [selectedNidError, setSelectedNidError] = useState<string | null>(null);

  // Reset the lazy NID state when the modal target changes.
  useEffect(() => {
    setSelectedNid(null);
    setSelectedNidError(null);
    setSelectedNidLoading(false);
  }, [selected?.id]);

  const revealVolunteerNid = async () => {
    if (!selected || selectedNidLoading || selectedNid !== null) return;
    setSelectedNidLoading(true);
    // Reset any previous error so a retry re-shows the loading state
    // instead of flickering between error + loading.
    setSelectedNidError(null);
    const { data, error } = await supabase.rpc(
      'decrypt_volunteer_nid_for_admin',
      { p_volunteer_id: selected.id },
    );
    setSelectedNidLoading(false);
    if (error) {
      // Mirror Batch 28's CommunityChatPage pattern — surface the
      // RPC diagnostic in the browser console so an operator with
      // DevTools open can see "could not find function" or
      // "Not authorized" instead of just the opaque Arabic UI
      // message. The UI message stays generic because displaying
      // 42501/42883 to a non-dev user is just noise.
      console.warn('decrypt_volunteer_nid_for_admin failed', error);
      setSelectedNidError(error.message);
      return;
    }
    setSelectedNid(typeof data === 'string' ? data : '');
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [total, setTotal] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const confirm = useConfirm();
  // U-ADM-06 · replace the native alert() with an inline notice on
  // the "invite to portal" success path (see setPortalNotice below).
  const [portalNotice, setPortalNotice] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  // Keep statusFilter in sync when the operator navigates between the
  // wrapper tabs (coordinator ↔ hr ↔ all). React-router reuses the same
  // VolunteersPage instance across these routes, so the initial-state
  // value we computed at mount would otherwise stay pinned on the tab
  // the operator opened first — causing the same rows to appear on
  // both queues.
  useEffect(() => {
    setStatusFilter(
      filterQueue === 'coordinator' ? 'REVIEW'
      : filterQueue === 'hr' ? 'HR'
      : 'ALL'
    );
  }, [filterQueue]);

  const load = async () => {
    setLoading(true);
    const [v, sp, go, br, count] = await Promise.all([
      // SPECS/002 Batch 35: the migration REVOKEs column-level
      // SELECT on `national_id` + `national_id_enc` from
      // `authenticated`, so `select('*')` silently returns the
      // visible columns and nothing else — any added column stays
      // readable here without us having to maintain an enumeration.
      // The only path to a plaintext NID is the audited
      // `decrypt_volunteer_nid_for_admin` RPC called from the modal.
      supabase.from('volunteers').select('*').order('created_at', { ascending: false }).limit(500),
      supabase.from('vol_specialties').select('id, name').order('name'),
      supabase.from('vol_governorates').select('id, name').order('name'),
      supabase.from('vol_branches').select('id, name').order('name'),
      supabase.from('volunteers').select('id', { count: 'exact', head: true }),
    ]);
    if (v.error) setError(v.error.message);
    else setRows((v.data ?? []) as Row[]);
    setSpecialties((sp.data ?? []) as Ref[]);
    setGovernorates((go.data ?? []) as Ref[]);
    setBranches((br.data ?? []) as Ref[]);
    setTotal(count.count ?? 0);
    setLoading(false);
  };

  // Unified fuzzy search (see admin/src/lib/search.ts) — same behavior
  // as the donations report: Arabic normalization, multi-word (all
  // words must appear), and every visible field is searchable so an
  // operator can find someone by name + phone tail, or by branch +
  // status label, or by external form code, in one input.
  const searchTokens = tokenizeQuery(search);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'ALL' && r.application_status !== statusFilter) return false;
      if (branchFilter !== 'ALL' && (r.branch_text ?? '') !== branchFilter) return false;
      if (govFilter !== 'ALL' && (r.governorate_text ?? '') !== govFilter) return false;
      if (searchTokens.length === 0) return true;
      const haystack = buildHaystack([
        r.full_name, r.phone, r.whatsapp_phone, r.email,
        r.external_form_code, r.branch_text, r.governorate_text,
        // SPECS/002: NID is no longer loaded into the row (encrypted
        // at rest, fetched on-demand via decrypt_volunteer_nid_for_admin
        // when the modal opens). It is intentionally not searchable
        // from this client-side filter — a dedicated server-side
        // "search by NID" would need to decrypt all rows, which is
        // the opposite of what the encryption is for.
        STATUS_LABEL[r.application_status],
      ]);
      return matchesAllTokens(haystack, searchTokens);
    });
  }, [rows, statusFilter, branchFilter, govFilter, search]);

  const uniqueBranchTexts = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => { if (r.branch_text) s.add(r.branch_text); });
    return Array.from(s).sort();
  }, [rows]);

  const uniqueGovTexts = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => { if (r.governorate_text) s.add(r.governorate_text); });
    return Array.from(s).sort();
  }, [rows]);

  const labelOf = (list: Ref[], id: number | null) =>
    id != null ? list.find((r) => r.id === id)?.name ?? `#${id}` : '—';

  const invitePortal = async (r: Row) => {
    if (!(await confirm({
      title: 'دعوة للبورتال',
      body: `هيوصل ${r.email} إيميل بلينك تحديد كلمة السر. بعد ما يحدّدها هيقدر يدخل يشوف أنشطته وساعاته.`,
      confirmText: 'ابعت',
    }))) return;
    setError(null);
    setPortalNotice(null);
    const { data, error: err } = await supabase.functions.invoke<{ ok?: boolean; error?: string }>(
      'volunteers-invite',
      { body: { volunteer_id: r.id } },
    );
    if (err || !data?.ok) {
      setError(data?.error ?? err?.message ?? 'حصل خطأ');
      return;
    }
    setPortalNotice(`تمام — الإيميل اتبعت لـ ${r.full_name}. المتطوع هيدخل، يحدد كلمة السر، وبعد كده يقدر يشوف الأنشطة وساعاته.`);
    await load();
  };

  const saveDecision = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    const { error: err } = await supabase.from('volunteers').update({
      application_status: selected.application_status,
      hr_note: selected.hr_note || null,
    }).eq('id', selected.id);
    setBusy(false);
    if (err) return setError(err.message);
    setSelected(null);
    await load();
  };

  // Quick approve/reject buttons for the coordinator and HR queues.
  // Approve advances the row to the next stage of the pipeline (REVIEW
  // → HR, HR → ACCEPTED), reject sends it to REJECTED. Once the state
  // is written the modal closes and the row leaves the queue tab.
  const quickApprove = async () => {
    if (!selected) return;
    setBusy(true);
    let payload: Record<string, unknown>;
    if (selected.application_status === 'REVIEW') {
      // Coordinator approves → moves to HR queue.
      payload = { application_status: 'HR' };
    } else if (selected.application_status === 'HR') {
      // HR approves → accept the application.
      payload = {
        application_status: 'ACCEPTED',
        hr_note: selected.hr_note || 'موافقة',
      };
    } else if (selected.application_status === 'STOPPED') {
      // Resume a stopped volunteer.
      payload = { application_status: 'ACCEPTED' };
    } else {
      setBusy(false);
      return;
    }
    const { error: err } = await supabase.from('volunteers').update(payload).eq('id', selected.id);
    setBusy(false);
    if (err) return setError(err.message);
    setSelected(null);
    await load();
  };

  const quickReject = async () => {
    if (!selected) return;
    setBusy(true);
    const payload: Record<string, unknown> = {
      application_status: 'REJECTED',
      hr_note: selected.hr_note || 'رفض',
    };
    const { error: err } = await supabase.from('volunteers').update(payload).eq('id', selected.id);
    setBusy(false);
    if (err) return setError(err.message);
    setSelected(null);
    await load();
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      // Send as raw text — the edge function handles multipart or plain text.
      const csvText = await file.text();
      const { data, error: err } = await supabase.functions.invoke<ImportResult>(
        'volunteers-import-csv',
        {
          body: csvText,
          headers: { 'Content-Type': 'text/csv; charset=utf-8' },
        },
      );
      if (err) setImportResult({ error: err.message });
      else setImportResult(data ?? { error: 'مافيش رد' });
    } catch (err: any) {
      setImportResult({ error: err?.message ?? String(err) });
    } finally {
      setImporting(false);
      if (fileInput.current) fileInput.current.value = '';
      await load();
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="التطوع"
        title={
          filterQueue === 'coordinator' ? 'موافقة مسؤول المتطوعين'
          : filterQueue === 'hr' ? 'موافقة الـ HR'
          : 'قائمة المتطوعين'
        }
        subtitle={
          filterQueue === 'coordinator'
            ? `طلبات في انتظار مسؤول المتطوعين — إجمالي: ${total}`
            : filterQueue === 'hr'
              ? `طلبات في انتظار موافقة الـ HR — إجمالي: ${total}`
              : `إجمالي: ${total} متطوع — فلتر بالحالة/الفرع/المحافظة أو بحث بالاسم/الموبايل/الكود`
        }
        actions={
          canManage ? (
            <label className="btn" style={{ cursor: 'pointer' }}>
              {importing ? 'جاري الاستيراد...' : 'استيراد CSV'}
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleImport}
                disabled={importing}
              />
            </label>
          ) : (
            <span className="badge">صلاحية عرض فقط</span>
          )
        }
      />
      {error && <div className="error-inline">{error}</div>}
      {portalNotice && (
        <div
          className="error-inline"
          style={{
            background: 'rgba(31,157,85,0.12)',
            color: 'var(--teal-dark)',
            border: '1px solid rgba(31,157,85,0.35)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span>{portalNotice}</span>
          <button
            type="button"
            className="btn btn-small btn-secondary"
            onClick={() => setPortalNotice(null)}
          >
            حسنًا
          </button>
        </div>
      )}
      {importResult && (
        importResult.ok ? (
          <div className="error-inline" style={{ background: 'rgba(31,157,85,0.12)', color: 'var(--teal-dark)', border: '1px solid rgba(31,157,85,0.35)' }}>
            تم — اتقرا {importResult.parsed_rows} صف، بعد إزالة المكرر {importResult.imported_after_dedup}،
            اتضافوا {importResult.inserted} متطوع جديد.
            {(importResult.skipped_duplicate_or_existing ?? 0) > 0 && ` (${importResult.skipped_duplicate_or_existing} كانوا موجودين قبل كده — اتخطونا).`}
            {(importResult.skipped_no_phone ?? 0) > 0 && ` (${importResult.skipped_no_phone} صف بدون اسم/موبايل — اتخطونا).`}
            {(importResult.errors?.length ?? 0) > 0 && (
              <div style={{ marginTop: 6, fontSize: 12 }}>أخطاء: {importResult.errors!.join(' — ')}</div>
            )}
          </div>
        ) : (
          <div className="error-inline">فشل الاستيراد: {importResult.error}</div>
        )
      )}

      {/* Filters unified into a single toolbar row that mirrors the
          donations page shape: status → branch → gov → search →
          result count. Status is a dropdown (not a pill row) so all
          admin filter surfaces read the same way. */}
      <div className="toolbar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'ALL' | Row['application_status'])}>
          {(['ALL', 'REVIEW', 'HR', 'ACCEPTED', 'REJECTED'] as const).map((s) => {
            const label = s === 'ALL' ? 'كل الطلبات' : STATUS_LABEL[s];
            const count = s === 'ALL' ? rows.length : rows.filter((r) => r.application_status === s).length;
            return (
              <option key={s} value={s}>
                {label} ({count})
              </option>
            );
          })}
        </select>
        <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
          <option value="ALL">كل الفروع</option>
          {uniqueBranchTexts.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={govFilter} onChange={(e) => setGovFilter(e.target.value)}>
          <option value="ALL">كل المحافظات</option>
          {uniqueGovTexts.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <input
          type="search"
          placeholder="بحث عن أي حاجة: اسم، موبايل، إيميل، كود، فرع، محافظة، حالة…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 240 }}
        />
        <div style={{ marginRight: 'auto', color: 'var(--text-secondary)', fontSize: 13 }}>
          {filtered.length} من {rows.length}{total > rows.length && ` (أخر ${rows.length} من ${total})`}
        </div>
      </div>

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="card empty">مفيش نتائج</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>الاسم</th>
                <th>الموبايل</th>
                <th>واتساب</th>
                <th>المحافظة / الفرع</th>
                <th>التخصص</th>
                <th>الحالة</th>
                <th>التواصل</th>
                <th>كود الاستمارة</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div>{r.full_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      <MaskedPii value={r.email} kind="email" />
                    </div>
                  </td>
                  <td><MaskedPii value={r.phone} kind="tel" style={{ direction: 'ltr' }} /></td>
                  <td><MaskedPii value={r.whatsapp_phone} kind="tel" style={{ direction: 'ltr' }} /></td>
                  <td>
                    {r.governorate_text ?? labelOf(governorates, r.governorate_id)}
                    {(r.branch_text || r.branch_id) && (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {r.branch_text ?? labelOf(branches, r.branch_id)}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 13 }}>{r.specialty_text ?? labelOf(specialties, r.specialty_id)}</td>
                  <td>
                    <span className={`badge ${r.application_status === 'ACCEPTED' ? 'badge-success' : r.application_status === 'REJECTED' ? 'badge-danger' : r.application_status === 'STOPPED' ? 'badge-muted' : 'badge-pending'}`}>
                      {STATUS_LABEL[r.application_status]}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {r.contacted ? (r.contacted_at ? formatDateTime(r.contacted_at) : 'اتم') : '—'}
                  </td>
                  <td style={{ direction: 'ltr', fontSize: 12 }}>{r.external_form_code ?? '—'}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-small" onClick={() => setSelected({ ...r })}>
                      {canManage ? 'فتح' : 'عرض'}
                    </button>
                    {canManage && r.application_status === 'ACCEPTED' && !r.user_id && r.email && (
                      <button className="btn btn-small btn-secondary" onClick={() => void invitePortal(r)}>
                        دعوة للبورتال
                      </button>
                    )}
                    {r.user_id && (
                      <span className="badge badge-success" title="متطوع مربوط بحساب البورتال">حسابه فعّال</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selected.full_name}</h2>
              <button className="btn btn-small btn-secondary" onClick={() => setSelected(null)}>إغلاق</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><strong>الموبايل:</strong> <MaskedPii value={selected.phone} kind="tel" style={{ direction: 'ltr' }} /></div>
              <div><strong>واتساب:</strong> <MaskedPii value={selected.whatsapp_phone} kind="tel" style={{ direction: 'ltr' }} /></div>
              <div><strong>موبايل ولي الأمر:</strong> <MaskedPii value={selected.guardian_phone} kind="tel" style={{ direction: 'ltr' }} /></div>
              <div><strong>الإيميل:</strong> <MaskedPii value={selected.email} kind="email" /></div>
              {/*
                U-ADM-01 · pre-launch audit hardening + SPECS/002.
                Egyptian national ID = 14 digits carrying DOB + gender +
                governorate + serial. Every section admin used to see
                every applicant's ID in plaintext.
                SPECS/002 (Batch 35) moved NID at rest to a bytea
                encrypted column and gates reads behind a SECURITY
                DEFINER RPC that writes an audit row on every call.
                MaskedPii keeps the reveal-for-10s UX from U-ADM-01.
                selectedNid: null = still loading; '' = no NID on
                file; non-empty = decrypted plaintext.
              */}
              <div>
                <strong>الرقم القومي:</strong>{' '}
                {selectedNid !== null ? (
                  <MaskedPii value={selectedNid || null} kind="id" style={{ direction: 'ltr' }} />
                ) : selectedNidLoading ? (
                  <span style={{ color: '#777' }}>جاري التحميل…</span>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-small btn-secondary"
                      onClick={revealVolunteerNid}
                    >
                      {selectedNidError ? 'إعادة المحاولة' : 'اعرض الرقم القومي'}
                    </button>
                    {selectedNidError && (
                      <span style={{ color: '#c62828', marginRight: 8 }}>
                        تعذّر فتح الرقم القومي
                      </span>
                    )}
                  </>
                )}
              </div>
              <div><strong>تاريخ الميلاد:</strong> <span style={{ direction: 'ltr' }}>{selected.registration_form_type ?? '—'}</span></div>
              <div><strong>التخصص:</strong> {selected.specialty_text ?? labelOf(specialties, selected.specialty_id)}</div>
              <div><strong>المحافظة:</strong> {selected.governorate_text ?? labelOf(governorates, selected.governorate_id)}</div>
              <div><strong>الفرع:</strong> {selected.branch_text ?? labelOf(branches, selected.branch_id)}</div>
              <div><strong>العنوان:</strong> {selected.address ?? '—'}</div>
              <div><strong>سيارة:</strong> {selected.has_car === true ? 'نعم' : selected.has_car === false ? 'لا' : '—'}
                {selected.has_car && ` (متاحة للتطوع: ${selected.car_available_for_volunteering === true ? 'نعم' : selected.car_available_for_volunteering === false ? 'لا' : '—'})`}
              </div>
              <div><strong>خبرات سابقة:</strong> {selected.prior_experience === true ? 'نعم' : selected.prior_experience === false ? 'لا' : '—'}</div>
              <div><strong>اتم التواصل:</strong> {selected.contacted ? (selected.contacted_at ? formatDateTime(selected.contacted_at) : 'نعم') : 'لا'}</div>
              <div><strong>نوع الاستمارة:</strong> {selected.registration_form_type ?? '—'}</div>
              <div><strong>كود الاستمارة:</strong> <span style={{ direction: 'ltr' }}>{selected.external_form_code ?? '—'}</span></div>
              <div><strong>ID خارجي:</strong> <span style={{ direction: 'ltr' }}>{selected.external_form_id ?? '—'}</span></div>
              {selected.days_and_committees && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong>الأيام واللجان:</strong>
                  <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{selected.days_and_committees}</div>
                </div>
              )}
              {selected.skills && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong>المهارات:</strong>
                  <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{selected.skills}</div>
                </div>
              )}
            </div>
            {/*
              U-ADM-04 · pre-launch audit hardening. Enforce the
              volunteer state machine:
                * REVIEW → HR                         (any admin)
                * HR     → ACCEPTED  (only if hr_decision=CONTINUE + hr_note filled)
                * HR     → REJECTED  (any admin; hr_note recommended)
                * any    → REVIEW    (bounce back for re-review)
              Previously the dropdown let any admin skip REVIEW → ACCEPTED
              with one click and no HR trace. Options that would violate
              the machine are `disabled` and the reason is inline.
            */}
            {/* Decision panel — buttons only, no dropdowns. Operator ask:
                "الموافقات تبقى ببوتون قبول أو رفض مش دروب ليست". Two big
                actions on REVIEW / HR queues; the HR note textarea stays
                as an optional context field, but the accept button on
                the HR stage requires it to be filled so a decision is
                never silent. Rows already ACCEPTED or REJECTED render
                as read-only summaries; STOP is available on ACCEPTED
                rows so HR can end a volunteer's participation. */}
            <div>
              {!canManage && (
                <div className="error-inline" style={{ marginBottom: 12, background: 'rgba(0,145,145,0.1)', color: 'var(--teal-dark)', border: '1px solid rgba(0,145,145,0.35)' }}>
                  دي صلاحية عرض فقط — مش هتقدر تعدل حالة المتطوع.
                </div>
              )}
              <div className="field">
                <label>
                  ملاحظات HR
                  {selected.application_status === 'HR' && (
                    <span style={{ color: 'var(--danger)', marginInlineStart: 6, fontSize: 12 }}>
                      *مطلوبة عند القبول النهائي
                    </span>
                  )}
                </label>
                <textarea
                  rows={3}
                  value={selected.hr_note ?? ''}
                  disabled={!canManage || selected.application_status === 'REJECTED'}
                  onChange={(e) => setSelected({ ...selected, hr_note: e.target.value })}
                  placeholder="سبب القبول / الرفض أو أي ملاحظة تخص المتطوع"
                />
              </div>
              {/* Read-only pipeline snapshot — single status chip. The
                  state machine is driven entirely by the buttons below;
                  the old dual "status + HR decision" split is gone
                  (operator ask 2026-08-28). */}
              <div className="field" style={{ marginBottom: 12 }}>
                <label>الحالة</label>
                <span className={`badge badge-${
                  selected.application_status === 'ACCEPTED' ? 'success'
                  : selected.application_status === 'REJECTED' ? 'muted'
                  : selected.application_status === 'STOPPED' ? 'muted'
                  : selected.application_status === 'HR' ? 'muted'
                  : 'pending'
                }`}>
                  {STATUS_LABEL[selected.application_status]}
                </span>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setSelected(null)}>
                  {canManage ? 'إغلاق' : 'إغلاق'}
                </button>
                {canActOnSelected(selected) && (selected.application_status === 'REVIEW' || selected.application_status === 'HR') && (
                  <>
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={busy}
                      onClick={() => void quickReject()}
                    >
                      {busy ? '...' : 'رفض'}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      style={{ background: '#1f9d55', color: '#fff' }}
                      disabled={
                        busy ||
                        (selected.application_status === 'HR' && !(selected.hr_note ?? '').trim())
                      }
                      onClick={() => void quickApprove()}
                      title={
                        selected.application_status === 'HR' && !(selected.hr_note ?? '').trim()
                          ? 'اكتب ملاحظات HR الأول'
                          : undefined
                      }
                    >
                      {busy
                        ? '...'
                        : selected.application_status === 'REVIEW'
                          ? 'موافقة (تحويل للـ HR)'
                          : 'موافقة نهائية (قبول)'}
                    </button>
                  </>
                )}
                {/* Accepted volunteer: HR can pause them (→ STOPPED). */}
                {canManage && selected.application_status === 'ACCEPTED' && (
                  <>
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        await supabase.from('volunteers').update({ application_status: 'STOPPED', hr_note: selected.hr_note || null }).eq('id', selected.id);
                        setBusy(false);
                        setSelected(null);
                        await load();
                      }}
                    >
                      إيقاف المتطوع
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        await supabase.from('volunteers').update({ hr_note: selected.hr_note || null }).eq('id', selected.id);
                        setBusy(false);
                        setSelected(null);
                        await load();
                      }}
                    >
                      حفظ ملاحظات HR
                    </button>
                  </>
                )}
                {/* Stopped volunteer: HR can resume them (→ ACCEPTED). */}
                {canManage && selected.application_status === 'STOPPED' && (
                  <button
                    type="button"
                    className="btn"
                    style={{ background: '#1f9d55', color: '#fff' }}
                    disabled={busy}
                    onClick={() => void quickApprove()}
                  >
                    إعادة تفعيل المتطوع
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
